import { createContext, ReactNode, useContext, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

type TourModeValue = { interactive: boolean; setInteractive: (value: boolean) => void };
const TourModeContext = createContext<TourModeValue | null>(null);

export type TourPageType = "room" | "task" | "game" | "score" | "interactive" | "other";
type TourPage = { path: string; type: TourPageType };

export const tourPages: TourPage[] = [
  { path: "/", type: "other" },
  { path: "/tarlatvezetes", type: "other" },
  { path: "/hogyan-mukodik", type: "interactive" },
  { path: "/rezidencia", type: "room" },
  { path: "/talalo", type: "room" },
  { path: "/talalo/feladat", type: "task" },
  { path: "/talalo/parositas", type: "game" },
  { path: "/talalo/restauralas", type: "game" },
  { path: "/talalo/eredmeny", type: "score" },
  { path: "/tukorterem", type: "room" },
  { path: "/tukorterem/vetites", type: "task" },
  { path: "/tukorterem/video-bal", type: "task" },
  { path: "/tukorterem/video-jobb", type: "task" },
  { path: "/tukorterem/konzol", type: "task" },
  { path: "/tukorterem/festmeny", type: "game" },
  { path: "/tukorterem/festmeny-2", type: "game" },
  { path: "/tukorterem/gyuruk", type: "game" },
  { path: "/tukorterem/eredmeny", type: "score" },
  { path: "/szalon", type: "room" },
  { path: "/szalon/peter", type: "task" },
  { path: "/szalon/terites", type: "task" },
  { path: "/szalon/radio", type: "task" },
  { path: "/szalon/video", type: "task" },
  { path: "/szalon/eredmeny", type: "score" },
  { path: "/petkiek", type: "room" },
  { path: "/petkiek/konyv", type: "task" },
  { path: "/petkiek/katona", type: "task" },
  { path: "/petkiek/csaladfa", type: "task" },
  { path: "/petkiek/eredmeny", type: "score" },
  { path: "/mariaffiek", type: "room" },
  { path: "/mariaffiek/peter", type: "task" },
  { path: "/mariaffiek/cimer", type: "task" },
  { path: "/mariaffiek/menekules/jatek", type: "game" },
  { path: "/mariaffiek/telefon", type: "task" },
  { path: "/mariaffiek/eredmeny", type: "score" },
  { path: "/vilaghaboru", type: "room" },
  { path: "/vilaghaboru/bombazas", type: "task" },
  { path: "/vilaghaboru/alak", type: "task" },
  { path: "/vilaghaboru/eredmeny", type: "score" },
  { path: "/extra", type: "room" },
  { path: "/befejezes", type: "other" },
];

function availableWithoutGames(type: TourPageType) {
  return type !== "game" && type !== "score" && type !== "interactive";
}

export function resolveTourPath(path: string, direction: 1 | -1, interactive: boolean) {
  if (interactive) return path;
  let index = tourPages.findIndex((page) => page.path === path);
  if (index < 0) return path;
  while (!availableWithoutGames(tourPages[index].type)) {
    index += direction;
    if (index < 0 || index >= tourPages.length) return path;
  }
  return tourPages[index].path;
}

export function TourModeProvider({ children }: { children: ReactNode }) {
  const [interactive, setInteractiveState] = useState(() => localStorage.getItem("mariaffi-interactive") !== "false");
  const setInteractive = (value: boolean) => {
    localStorage.setItem("mariaffi-interactive", String(value));
    setInteractiveState(value);
  };
  return <TourModeContext.Provider value={{ interactive, setInteractive }}>{children}</TourModeContext.Provider>;
}

export function useTourMode() {
  const value = useContext(TourModeContext);
  if (!value) throw new Error("A useTourMode csak TourModeProvider alatt használható.");
  return value;
}

export function TourModeGuard({ children }: { children: ReactNode }) {
  const { interactive } = useTourMode();
  const { pathname } = useLocation();
  if (!interactive) {
    const page = tourPages.find((item) => item.path === pathname);
    if (page && !availableWithoutGames(page.type)) {
      return <Navigate to={resolveTourPath(pathname, 1, false)} replace />;
    }
  }
  return <>{children}</>;
}
